import React from "react";
import { MDXRemote, type MDXRemoteSerializeResult } from "next-mdx-remote";
import {
  AlertTriangle,
  Check,
  Info,
  Lightbulb,
  Minus,
  Trophy,
  X,
} from "lucide-react";
import type { ContentBlock, ContentMatrix } from "@/content/types";
import { H2, STEP_COMPONENTS } from "./prose";

// The callout, feature matrix, verdict cards and bar chart blocks
// (content/types.ts). Jammable is highlighted wherever it appears, the same
// way the comparison table marks its row.

const isUs = (name: string) => name.trim().toLowerCase() === "jammable";

const TONES = {
  tip: {
    icon: Lightbulb,
    box: "tw-border-[#C9EE3A] tw-bg-[#F8FFDD]",
    badge: "tw-bg-jam-lime tw-text-jam-ink",
  },
  note: {
    icon: Info,
    box: "tw-border-jam-soft tw-bg-jam-lav",
    badge: "tw-bg-jam-purple tw-text-white",
  },
  warning: {
    icon: AlertTriangle,
    box: "tw-border-[#F5C36B] tw-bg-[#FFF6E5]",
    badge: "tw-bg-[#F5A524] tw-text-jam-ink",
  },
};

export function Callout({
  block,
  mdx,
}: {
  block: Extract<ContentBlock, { type: "callout" }>;
  mdx?: MDXRemoteSerializeResult;
}) {
  const tone = TONES[block.tone];
  const Icon = tone.icon;
  return (
    <aside
      className={`tw-my-7 tw-flex tw-gap-4 tw-rounded-[22px] tw-border-2 tw-border-solid tw-p-5 md:tw-p-6 ${tone.box}`}
    >
      <span
        className={`tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full ${tone.badge}`}
      >
        <Icon size={19} strokeWidth={2.6} />
      </span>
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5 tw-text-[15px]">
        <p className="tw-m-0 tw-font-extrabold tw-text-jam-ink">
          {block.title}
        </p>
        {mdx && <MDXRemote {...mdx} components={STEP_COMPONENTS} />}
      </div>
    </aside>
  );
}

function Mark({ value }: { value: string }) {
  const v = value.trim().toLowerCase();
  const circle =
    "tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full";
  if (v === "yes")
    return (
      <span className={`${circle} tw-bg-jam-lime tw-text-jam-ink`} title="Yes">
        <Check size={16} strokeWidth={3} aria-label="Yes" />
      </span>
    );
  if (v === "no")
    return (
      <span className={`${circle} tw-bg-jam-lav tw-text-jam-muted`} title="No">
        <X size={15} strokeWidth={3} aria-label="No" />
      </span>
    );
  if (v === "partial")
    return (
      <span
        className={`${circle} tw-bg-[#FFE8B8] tw-text-[#8A5A00]`}
        title="Partly"
      >
        <Minus size={16} strokeWidth={3} aria-label="Partly" />
      </span>
    );
  return (
    <span className="tw-text-[13px] tw-font-bold tw-leading-snug tw-text-[#3d3a4b]">
      {value}
    </span>
  );
}

export function Matrix({
  heading,
  matrix,
}: {
  heading?: string;
  matrix: ContentMatrix;
}) {
  const icons = matrix.rows.some((r) =>
    r.values.some((v) => ["yes", "no", "partial"].includes(v.toLowerCase())),
  );
  return (
    <>
      {heading && <H2>{heading}</H2>}
      <figure className="tw-mx-0 tw-my-6">
        <div className="tw-overflow-x-auto tw-rounded-[24px] tw-border-2 tw-border-solid tw-border-jam-ink tw-bg-white">
          <table
            className="tw-w-full tw-border-collapse tw-text-left tw-text-sm"
            style={{ minWidth: 200 + matrix.tools.length * 110 }}
          >
            <thead>
              <tr className="tw-bg-jam-ink">
                <th
                  scope="col"
                  className="tw-sticky tw-left-0 tw-z-[1] tw-bg-jam-ink tw-px-4 tw-py-3.5 tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.08em] tw-text-white/70"
                >
                  Feature
                </th>
                {matrix.tools.map((t) => (
                  <th
                    key={t}
                    scope="col"
                    className={`tw-px-3 tw-py-3.5 tw-text-center tw-text-[13px] tw-font-extrabold ${
                      isUs(t)
                        ? "tw-bg-jam-lime tw-text-jam-ink"
                        : "tw-text-white"
                    }`}
                  >
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((r) => (
                <tr
                  key={r.feature}
                  className="tw-border-0 tw-border-t tw-border-solid tw-border-jam-line"
                >
                  <th
                    scope="row"
                    className="tw-sticky tw-left-0 tw-z-[1] tw-bg-white tw-px-4 tw-py-3 tw-font-extrabold tw-text-jam-ink"
                  >
                    {r.feature}
                  </th>
                  {r.values.map((v, i) => (
                    <td
                      key={i}
                      className={`tw-px-3 tw-py-3 tw-text-center ${
                        isUs(matrix.tools[i] ?? "") ? "tw-bg-[#F8FFDD]" : ""
                      }`}
                    >
                      <Mark value={v} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(matrix.caption || icons) && (
          <figcaption className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-text-xs tw-font-bold tw-text-jam-muted">
            {icons && (
              <span className="tw-inline-flex tw-items-center tw-gap-3">
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                  <Check size={13} strokeWidth={3} /> Yes
                </span>
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                  <Minus size={13} strokeWidth={3} /> Partly
                </span>
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                  <X size={13} strokeWidth={3} /> No
                </span>
              </span>
            )}
            {matrix.caption && <span>{matrix.caption}</span>}
          </figcaption>
        )}
      </figure>
    </>
  );
}

export function Verdicts({
  block,
}: {
  block: Extract<ContentBlock, { type: "verdicts" }>;
}) {
  return (
    <>
      {block.heading && <H2>{block.heading}</H2>}
      <ul className="tw-my-6 tw-grid tw-list-none tw-grid-cols-1 tw-gap-3 tw-p-0 sm:tw-grid-cols-2">
        {block.items.map((v) => {
          const ours = isUs(v.pick);
          return (
            <li
              key={v.useCase}
              className={`tw-flex tw-flex-col tw-gap-2 tw-rounded-[22px] tw-p-5 ${
                ours ? "tw-bg-jam-ink tw-text-white" : "tw-bg-jam-lav"
              }`}
            >
              <span
                className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-extrabold tw-uppercase tw-tracking-[0.12em] ${
                  ours ? "tw-text-white/70" : "tw-text-jam-purple"
                }`}
              >
                <Trophy size={13} strokeWidth={2.6} />
                Best for {v.useCase}
              </span>
              <span
                className={`jam-display tw-text-[24px] tw-font-extrabold tw-leading-none ${
                  ours ? "tw-text-jam-lime" : "tw-text-jam-ink"
                }`}
              >
                {v.pick}
              </span>
              <p
                className={`tw-m-0 tw-text-sm tw-font-semibold tw-leading-relaxed ${
                  ours ? "tw-text-white/80" : "tw-text-jam-muted"
                }`}
              >
                {v.why}
              </p>
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function Bars({
  block,
}: {
  block: Extract<ContentBlock, { type: "bars" }>;
}) {
  const max = Math.max(...block.items.map((i) => i.value), 1);
  return (
    <>
      {block.heading && <H2>{block.heading}</H2>}
      <figure className="tw-mx-0 tw-my-6 tw-rounded-[24px] tw-bg-jam-lav tw-p-4 md:tw-p-6">
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
          {block.items.map((i) => (
            <li
              key={i.label}
              className="tw-grid tw-grid-cols-1 tw-gap-1.5 md:tw-grid-cols-[170px_1fr] md:tw-items-center md:tw-gap-4"
            >
              <span className="tw-text-sm tw-font-extrabold tw-text-jam-ink">
                {i.label}
              </span>
              <span className="tw-block tw-h-10 tw-overflow-hidden tw-rounded-full tw-bg-white">
                <span
                  className="tw-flex tw-h-full tw-items-center tw-justify-end tw-rounded-full tw-bg-[linear-gradient(90deg,#782DC8,#2E0F52)] tw-px-4 tw-text-[13px] tw-font-extrabold tw-text-white"
                  style={{
                    width: `${Math.max(34, Math.round((i.value / max) * 100))}%`,
                  }}
                >
                  <span className="tw-whitespace-nowrap">{i.display}</span>
                </span>
              </span>
            </li>
          ))}
        </ul>
        {block.caption && (
          <figcaption className="tw-mt-4 tw-text-xs tw-font-bold tw-text-jam-muted">
            {block.caption}
          </figcaption>
        )}
      </figure>
    </>
  );
}
